<template>
    <div class="table-container w-full">
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="vehicle-portfolio-checker-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'status'">
                            <a-tag v-if="text == 'pending'" color="orange">
                                {{ $t('cia.vehicle_checker.labels.pending') }}
                            </a-tag>
                            <a-tag v-if="text == 'running'" color="blue">
                                {{ $t('cia.vehicle_checker.labels.running') }}
                            </a-tag>
                            <a-tag v-if="text == 'completed'" color="green">
                                {{ $t('cia.vehicle_checker.labels.completed') }}
                            </a-tag>
                        </template>
                        <template v-else-if="column.key === 'timeago'">{{ timeAgo(text) }}</template>
                        <template v-else-if="column.key === 'report'">
                            <a @click="downloadVPCReport(record.id)" class="underline">
                                {{ record.report }}
                            </a>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import FileManager from '@/utils/file.js';

export default {
    data() {
        return {
            data: [],
            search_value: null,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            current_filter: {},
            sorter: {},
            params: {},
            checking_refreshes: {},
        };
    },
    mounted() {
        this.startCycle();
    },
    beforeDestroy() {
        clearInterval(this.checking_refreshes);
    },
    computed: {
        getColumns() {
            return [
                {
                    title: this.$t('globals.tables.vehicle_portfolio_checker.headers.upload_file'),
                    key: 'upload_file',
                    dataIndex: 'upload_file',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.vehicle_portfolio_checker.headers.status'),
                    key: 'status',
                    dataIndex: 'status',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.vehicle_portfolio_checker.headers.report'),
                    key: 'report',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.vehicle_portfolio_checker.headers.started_at'),
                    key: 'timeago',
                    dataIndex: 'started_at',
                    defaultSortOrder: 'descend',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.vehicle_portfolio_checker.headers.completed_at'),
                    key: 'timeago',
                    dataIndex: 'completed_at',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
    },
    methods: {
        startCycle() {
            this.fetch();

            this.checking_refreshes = setInterval(() => {
                this.reload();
            }, 60000);
        },
        timeAgo(datetime) {
            let mjs = this.$dayjs(datetime);

            return mjs.isValid() ? mjs.fromNow() : '';
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}) {
            this.loading = true;
            this.params = {
                search_value: this.params.search_value ? this.params.search_value : null,
                ...params,
            };

            this.$http
                .get('/api/reports/vehicle-porfolio-checker/list', {
                    params: {
                        ...this.params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVPCL01' }),
                        });
                    }
                });
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.pagination.current = 1;

                this.fetch({
                    search_value: this.search_value,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        downloadVPCReport(id) {
            this.$http
                .post('/api/reports/vehicle-porfolio-checker/download', {
                    id: id,
                })
                .then((response) => {
                    if (
                        _.includes(
                            [
                                'text/csv; charset=UTF-8',
                                'text/csv; charset=utf-8',
                                'text/plain; charset=UTF-8',
                                'text/plain; charset=utf-8',
                            ],
                            response.headers['content-type'],
                        )
                    ) {
                        this.$notification.success({
                            message: this.$t('cia.vehicle_checker.notifications.success.download'),
                        });

                        FileManager.download(response);
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVPCD01' }),
                        });
                    }
                });
        },
    },
};
</script>
